import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { Fragment } from 'react';
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceArea,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import Icon from '/@/components/icon';
import colors from '/@/components/theme-provider/colors';
import useUnitContext from '/@/core/context/unit';
import useWindowSize from '/@/utils/hooks/window-size';
import Title from '/@/v3/components/title';

/**
 * @typedef {typeof import('components/crop-monitoring/assets/graphicData').transformData} TransformDataFunc
 *
 * @param {{
 * dataGraphic: ReturnType<TransformDataFunc>
 * type: Parameters<TransformDataFunc>[1]
 * }}
 */
const LineGraphic = ({
    dataGraphic,
    type,
    width = '100%',
    height = 250,
    top = 20,
    bottom = 0,
    domain = [0, 100],
    showXAxis = true,
    showYAxis = false,
    max,
    min,
}) => {
    const windowWidth = useWindowSize().width;
    const isMobile = windowWidth <= 900;
    const isDesktopSm = windowWidth <= 1024;

    return (
        <ResponsiveContainer width={width} height={height}>
            <ComposedChart
                width={width}
                height={height}
                data={dataGraphic}
                margin={{ top: top, right: 25, left: 15, bottom: bottom }}
            >
                <CartesianGrid strokeDasharray='1 1' vertical={false} />
                {showXAxis && (
                    <XAxis
                        dataKey='data'
                        tickLine={false}
                        axisLine={false}
                        interval={
                            isMobile
                                ? Math.round(dataGraphic.length / 5)
                                : isDesktopSm
                                  ? Math.round(dataGraphic.length / 10)
                                  : Math.round(dataGraphic.length / 20)
                        }
                        style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: colors.gray.default,
                        }}
                    />
                )}
                {showYAxis && (
                    <YAxis
                        dataKey='dataToolTip'
                        tickLine={false}
                        axisLine={false}
                        interval='preserveStartEnd'
                        type='number'
                        domain={domain}
                        width={25}
                        style={{
                            fontSize: '10px',
                            fontWeight: 'bold',
                            color: colors.gray.default,
                        }}
                    />
                )}
                <Tooltip payload={dataGraphic} content={<CustomTooltip type={type} />} />
                {max && (
                    <Fragment>
                        <ReferenceLine
                            y={max}
                            label={{
                                position: 'right',
                                value: `${max}`,
                                fill: 'red',
                                style: { fontSize: 12, fontWeight: 600 },
                            }}
                            stroke='red'
                            strokeDasharray='3 3'
                        />
                        <ReferenceArea y1={max} y2={domain[1]} fill='#FFE6E6' />
                    </Fragment>
                )}

                {min && (
                    <Fragment>
                        <ReferenceLine
                            y={min}
                            label={{
                                position: 'right',
                                value: `${min}`,
                                fill: 'cornflowerblue',
                                style: { fontSize: 12, fontWeight: 600 },
                            }}
                            stroke='cornflowerblue'
                            strokeDasharray='3 3'
                        />
                        <ReferenceArea y1={min} y2={domain[0]} fill='#F0F6FF' />
                    </Fragment>
                )}
                <Line
                    type='monotone'
                    dataKey={type}
                    stroke={colors.themes.cultivio.primary.default}
                    fillOpacity={1}
                    fill='url(#colorPv)'
                    strokeWidth={2}
                    dot={false}
                />
                {type === 'temperature' && (
                    <Fragment>
                        <Line
                            type='monotone'
                            dataKey='temperatureMax'
                            stroke='red'
                            fillOpacity={1}
                            fill='url(#colorPv)'
                            strokeWidth={1}
                            dot={false}
                        />
                        <Line
                            type='monotone'
                            dataKey='temperatureMin'
                            stroke='cornflowerblue'
                            fillOpacity={1}
                            fill='url(#colorPv)'
                            strokeWidth={1}
                            dot={false}
                        />
                    </Fragment>
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default LineGraphic;

/** @param {{type: Parameters<TransformDataFunc>[1]}} */
const CustomTooltip = ({ active, payload, type, ...rest }) => {
    const units = useUnitContext();
    const typeData = {
        temperature: units.temperature,
        wind: units.speed,
        rain: units.rain,
        humidity: '%',
    };

    const typeIcon = {
        temperature: 32,
        wind: 23,
        rain: 39,
        humidity: 9,
    };

    if (active && payload && payload.length) {
        return (
            <Grid
                container
                sx={{
                    borderRadius: 2,
                    border: '0.5px solid #E1E1E1',
                    backgroundColor: 'white!important',
                    width: 100,
                    pt: 1,
                }}
            >
                <Grid item xs={12} sx={{ borderBottom: '0.5px solid #E1E1E1', p: 1 }}>
                    <Grid container alignItems='center' flexDirection='row' sx={{ gap: 1 }}>
                        <Grid item>
                            <span style={{ fontSize: '14px' }} className='material-icons'>
                                calendar_month
                            </span>
                        </Grid>
                        <Grid item>
                            <Title size='sm' bolder>
                                {Boolean(payload.length) && payload[0]['payload']['textTooltip']}
                            </Title>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} sx={{ p: 1 }}>
                    <Grid container alignItems='center' sx={{ gap: 1 }}>
                        <Grid item>
                            {type === 'humidity' ? (
                                <Icon size='xxs' type='humidityVector' />
                            ) : (
                                <img
                                    src={`https://doc.media.weather.com/products/icons/${(
                                        '0' + typeIcon[type] || 32
                                    ).slice(-2)}.png`}
                                    alt='Icon climate from The Weather Company'
                                    width={13}
                                    height={13}
                                />
                            )}
                        </Grid>
                        <Grid item xs='auto'>
                            {type === 'temperature' && (
                                <Box
                                    component='p'
                                    sx={{ fontWeight: 800, fontSize: '12px', color: (t) => t.palette.error.main }}
                                >{`${payload?.[0]?.payload.temperatureMax} ${typeData[type] || ''}`}</Box>
                            )}
                            <p style={{ fontWeight: 800, fontSize: '12px' }}>{`${payload?.[0]?.value} ${
                                typeData[type] || ''
                            }`}</p>
                            {type === 'temperature' && (
                                <Box
                                    component='p'
                                    sx={{ fontWeight: 800, fontSize: '12px', color: 'cornflowerblue' }}
                                >{`${payload?.[0]?.payload.temperatureMin} ${typeData[type] || ''}`}</Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    return null;
};
